import { apiRequest } from "./client";
import type { AcademyRole } from "../types/user";

export type ArticleStatus = "draft" | "published" | "archived";
export type ArticleType = "article" | "regulation" | "script" | "checklist";

export const ARTICLE_TYPE_LABEL: Record<ArticleType, string> = {
  article: "Статья",
  regulation: "Регламент",
  script: "Скрипт",
  checklist: "Чек-лист",
};

export interface CategoryNode {
  id: number;
  parent_id: number | null;
  name: string;
  slug: string;
  position: number;
  children: CategoryNode[];
  articles_count: number;
}

export interface ArticleListItem {
  id: number;
  title: string;
  slug: string;
  status: ArticleStatus;
  article_type: ArticleType;
  category_id: number | null;
  category_name: string | null;
  updated_at: string;
  published_at: string | null;
}

export interface ArticleDetail extends ArticleListItem {
  content_markdown: string;
  author_user_id: number | null;
  allowed_roles: AcademyRole[];
  created_at: string;
}

export interface SearchHit {
  id: number;
  title: string;
  slug: string;
  category_name: string | null;
  snippet: string;
  rank: number;
}

export interface SearchResults {
  items: SearchHit[];
  total: number;
  query: string;
}

// Categories

export function listCategories(): Promise<{ items: CategoryNode[] }> {
  return apiRequest<{ items: CategoryNode[] }>("/kb/categories");
}

export function createCategory(payload: {
  name: string;
  slug: string;
  parent_id?: number | null;
}): Promise<CategoryNode> {
  return apiRequest<CategoryNode>("/kb/categories", { method: "POST", body: payload });
}

export function updateCategory(
  id: number,
  payload: { name?: string; parent_id?: number | null },
): Promise<CategoryNode> {
  return apiRequest<CategoryNode>(`/kb/categories/${id}`, { method: "PATCH", body: payload });
}

export function deleteCategory(id: number): Promise<void> {
  return apiRequest<void>(`/kb/categories/${id}`, { method: "DELETE" });
}

// Articles

export function listArticles(params: {
  category_id?: number;
  status?: ArticleStatus;
  type?: ArticleType;
  q?: string;
} = {}): Promise<{ items: ArticleListItem[]; total: number }> {
  const u = new URLSearchParams();
  if (params.category_id) u.set("category_id", String(params.category_id));
  if (params.status) u.set("status", params.status);
  if (params.type) u.set("type", params.type);
  if (params.q) u.set("q", params.q);
  const s = u.toString();
  return apiRequest<{ items: ArticleListItem[]; total: number }>(
    `/kb/articles${s ? `?${s}` : ""}`,
  );
}

export function getArticle(id: number): Promise<ArticleDetail> {
  return apiRequest<ArticleDetail>(`/kb/articles/${id}`);
}

export function createArticle(payload: {
  title: string;
  slug: string;
  content_markdown?: string;
  article_type?: ArticleType;
  category_id?: number;
  allowed_roles?: AcademyRole[];
}): Promise<ArticleDetail> {
  return apiRequest<ArticleDetail>("/kb/articles", { method: "POST", body: payload });
}

export function updateArticle(
  id: number,
  payload: Partial<{
    title: string;
    content_markdown: string;
    article_type: ArticleType;
    category_id: number | null;
    allowed_roles: AcademyRole[];
  }>,
): Promise<ArticleListItem> {
  return apiRequest<ArticleListItem>(`/kb/articles/${id}`, { method: "PATCH", body: payload });
}

export function publishArticle(id: number): Promise<ArticleListItem> {
  return apiRequest<ArticleListItem>(`/kb/articles/${id}/publish`, { method: "POST" });
}

export function archiveArticle(id: number): Promise<ArticleListItem> {
  return apiRequest<ArticleListItem>(`/kb/articles/${id}/archive`, { method: "POST" });
}

export function deleteArticle(id: number): Promise<void> {
  return apiRequest<void>(`/kb/articles/${id}`, { method: "DELETE" });
}

// Search

export function searchKb(q: string): Promise<SearchResults> {
  const u = new URLSearchParams({ q });
  return apiRequest<SearchResults>(`/kb/search?${u.toString()}`);
}
